"use client"

import { useState, useEffect } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Search, ChevronDown, ArrowUpDown, Eye, Check } from "lucide-react"
import { GlobalPagination } from "@/components/global-pagination"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { AutoApproveStatusHint } from "@/components/auto-approve-status-hint"
import { ConnectionBenefitsCell } from "@/components/connection-benefits-cell"
import { ConnectionDetailsModal } from "@/components/connection-details-modal"
import {
  connectionStatusStyles,
  formatConnectionForDisplay,
  isAutoApproveCountdownLabel,
  STATUS_FILTER_OPTIONS,
  LINK_STATUS_FILTER_OPTIONS,
  STAGE_TO_FILTER_VALUE,
} from "@/lib/connection-display-utils"
import { cn } from "@/lib/utils"
import { ConnectionLinkInformationCell, FullUrlLink } from "@/components/connection-url-display"

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-block whitespace-nowrap px-2 py-0.5 text-xs font-medium rounded border ${connectionStatusStyles[status] || "bg-gray-50 text-gray-600 border-gray-200"}`}
    >
      {status}
    </span>
  )
}

interface LinksDetailsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  orderId: string | number
  connections: any[]
  zsOppFee?: number
}

const DEFAULT_PAGE_SIZE = 10

export function LinksDetailsModal({
  open,
  onOpenChange,
  orderId,
  connections,
  zsOppFee = 150,
}: LinksDetailsModalProps) {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [selectedConnection, setSelectedConnection] = useState<any>(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [linkStatusFilter, setLinkStatusFilter] = useState("all")

  const displayConnections = (connections ?? []).map((conn) => ({ ...formatConnectionForDisplay(conn, zsOppFee), rawConn: conn }))
  const filteredConnections = displayConnections.filter((conn) => {
    const matchesSearch = !searchTerm.trim() || (() => {
      const q = searchTerm.trim().toLowerCase()
      return (
        (conn.org || "").toLowerCase().includes(q) ||
        (conn.opportunity || "").toLowerCase().includes(q) ||
        (conn.anchor || "").toLowerCase().includes(q) ||
        (conn.benefitsLines ?? []).some((line) => line.toLowerCase().includes(q))
      )
    })()
    const filterStageVal = conn.stageNum != null ? STAGE_TO_FILTER_VALUE[conn.stageNum] ?? null : null
    const matchesStatus = statusFilter === "all" || filterStageVal === statusFilter
    const matchesLinkStatus =
      linkStatusFilter === "all" ||
      (linkStatusFilter === "live" && conn.linkLive) ||
      (linkStatusFilter === "not-live" && conn.linkNotLive) ||
      (linkStatusFilter === "link-down" && conn.linkDown)
    return matchesSearch && matchesStatus && matchesLinkStatus
  })
  const total = filteredConnections.length
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const startIdx = (currentPage - 1) * pageSize
  const paginatedConnections = filteredConnections.slice(startIdx, startIdx + pageSize)

  useEffect(() => {
    if (open) {
      setCurrentPage(1)
      setSelectedConnection(null)
      setSearchTerm("")
      setStatusFilter("all")
      setLinkStatusFilter("all")
    }
  }, [open])

  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, statusFilter, linkStatusFilter, connections])

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
          className="max-w-[90rem] max-h-[90vh] flex flex-col p-0 gap-0"
          onInteractOutside={selectedConnection ? (e) => e.preventDefault() : undefined}
          onPointerDownOutside={selectedConnection ? (e) => e.preventDefault() : undefined}
          onEscapeKeyDown={selectedConnection ? (e) => e.preventDefault() : undefined}
        >
        <DialogHeader className="px-6 py-5 border-b border-border shrink-0">
          <DialogTitle>Links for Order #{orderId}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
          <div className="px-6 py-5 border-b border-border shrink-0">
            <div className="flex flex-wrap gap-4">
              <div className="relative flex-1 min-w-[280px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by organization name, opportunity name, or anchor text..."
                  className="pl-9"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="gap-2 bg-transparent min-w-[180px] justify-between">
                    {STATUS_FILTER_OPTIONS.find((o) => o.value === statusFilter)?.label ?? "All Statuses"}
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {STATUS_FILTER_OPTIONS.map((opt) => (
                    <DropdownMenuItem
                      key={opt.value}
                      onClick={() => setStatusFilter(opt.value)}
                      className="flex items-center justify-between"
                    >
                      {opt.label}
                      {statusFilter === opt.value && <Check className="w-4 h-4" />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="gap-2 bg-transparent min-w-[180px] justify-between">
                    {LINK_STATUS_FILTER_OPTIONS.find((o) => o.value === linkStatusFilter)?.label ?? "All Link Statuses"}
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {LINK_STATUS_FILTER_OPTIONS.map((opt) => (
                    <DropdownMenuItem
                      key={opt.value}
                      onClick={() => setLinkStatusFilter(opt.value)}
                      className="flex items-center justify-between"
                    >
                      {opt.label}
                      {linkStatusFilter === opt.value && <Check className="w-4 h-4" />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <div className="overflow-x-auto overflow-y-auto flex-1 max-h-[600px]">
            <table className="w-full text-sm">
              <thead className="bg-muted border-b border-border sticky top-0 z-20">
                <tr>
                  <th className="text-center px-4 py-4 font-medium text-muted-foreground whitespace-nowrap">
                    Details
                  </th>
                  <th className="text-left px-4 py-4 font-medium text-muted-foreground whitespace-nowrap">
                    Org & Opportunity
                  </th>
                  <th className="text-left px-4 py-4 font-medium text-muted-foreground min-w-[12rem] max-w-[28rem]">
                    Source
                  </th>
                  <th className="text-left px-4 py-4 font-medium text-muted-foreground min-w-[12rem] max-w-md">
                    Link Information
                  </th>
                  <th className="text-left px-4 py-4 font-medium text-muted-foreground whitespace-nowrap">
                    Address
                  </th>
                  <th className="text-right px-4 py-4 font-medium text-muted-foreground whitespace-nowrap">
                    Cost
                  </th>
                  <th className="text-left px-4 py-4 font-medium text-muted-foreground whitespace-nowrap">
                    SEO Metrics
                  </th>
                  <th className="text-left px-4 py-4 font-medium text-muted-foreground min-w-[8rem] max-w-[11rem]">
                    Benefits
                  </th>
                  <th className="text-left px-4 py-4 font-medium text-muted-foreground whitespace-nowrap">
                    <span className="inline-flex items-center gap-1">
                      Status
                      <ArrowUpDown className="w-3.5 h-3.5 text-foreground" />
                    </span>
                  </th>
                  <th className="text-left px-4 py-4 font-medium text-muted-foreground whitespace-nowrap">
                    Payment
                  </th>
                  <th className="text-left px-4 py-4 font-medium text-muted-foreground whitespace-nowrap">
                    Link Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {displayConnections.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="px-6 py-12 text-center text-muted-foreground text-sm">
                      No connections for this order
                    </td>
                  </tr>
                ) : filteredConnections.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="px-6 py-12 text-center text-muted-foreground text-sm">
                      No connections match your filters
                    </td>
                  </tr>
                ) : (
                  paginatedConnections.map((conn) => (
                    <tr key={conn.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3 text-center">
                        <button
                          type="button"
                          onClick={() => setSelectedConnection(conn.rawConn)}
                          className="inline-flex items-center justify-center p-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                          aria-label={`View details for ${conn.org}`}
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                      </td>
                      <td className="px-4 py-3 max-w-[220px]">
                        <div className="font-medium text-foreground truncate">{conn.org}</div>
                        <div className="text-muted-foreground text-xs truncate">{conn.opportunity}</div>
                      </td>
                      <td className="px-4 py-3 min-w-0 max-w-md align-middle">
                        <FullUrlLink url={conn.sourceUrl} />
                      </td>
                      <td className="px-4 py-3 min-w-0 max-w-md align-top">
                        <ConnectionLinkInformationCell targetUrl={conn.targetUrl} anchor={conn.anchor} />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-foreground text-xs">{conn.address}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <span className="font-semibold text-foreground">{conn.costFormatted}</span>
                        {conn.total > 0 && (
                          <div>
                            <Popover>
                              <PopoverTrigger asChild>
                                <button type="button" className="text-[10px] text-blue-600 hover:underline">
                                  View
                                </button>
                              </PopoverTrigger>
                              <PopoverContent className="w-64" align="end">
                                <div className="space-y-1.5 text-sm">
                                  <h4 className="font-semibold text-foreground mb-2">Cost Breakdown</h4>
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Sponsorship</span>
                                    <span className="text-foreground">${conn.sponsorship.toLocaleString()}</span>
                                  </div>
                                  {conn.processingFee > 0 && (
                                    <div className="flex justify-between">
                                      <span className="text-muted-foreground">Processing Fee{conn.processingFeePct > 0 ? ` (${conn.processingFeePct}%)` : ""}</span>
                                      <span className="text-foreground">${conn.processingFee.toLocaleString()}</span>
                                    </div>
                                  )}
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">ZS Fee</span>
                                    <span className="text-foreground">${conn.zsFee.toLocaleString()}</span>
                                  </div>
                                  <div className="flex justify-between pt-1.5 border-t border-dashed border-border">
                                    <span className="font-medium text-foreground">Total</span>
                                    <span className="font-bold text-foreground">${conn.total.toLocaleString()}</span>
                                  </div>
                                </div>
                              </PopoverContent>
                            </Popover>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 align-middle text-[10px]">
                        <div className="space-y-0.5 text-muted-foreground">
                          <div>
                            <span className="font-medium text-foreground/90">DA</span>{" "}
                            {conn.seoDa != null ? conn.seoDa : "—"}
                          </div>
                          <div>
                            <span className="font-medium text-foreground/90">DR</span>{" "}
                            {conn.seoDr != null ? conn.seoDr : "—"}
                          </div>
                          <div>
                            <span className="font-medium text-foreground/90">Traffic</span>{" "}
                            {conn.seoTraffic ?? "—"}
                          </div>
                          <div>
                            <span className="font-medium text-foreground/90">Spam</span>{" "}
                            {conn.seoSpam != null ? conn.seoSpam : "—"}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top min-w-0 max-w-[11rem]">
                        <ConnectionBenefitsCell lines={conn.benefitsLines} contextLabel={conn.org} />
                      </td>
                      <td
                        className={`px-4 py-3 align-middle ${conn.status === "—" && conn.statusDate === "—" ? "text-center" : ""}`}
                      >
                        {conn.status === "—" && conn.statusDate === "—" ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          <div className="flex w-full min-w-0 flex-col items-center justify-center gap-1.5">
                            {conn.status === "—" ? (
                              <span className="text-muted-foreground text-sm">—</span>
                            ) : (
                              <StatusBadge status={conn.status} />
                            )}
                            <div
                              className={cn(
                                "text-center text-muted-foreground",
                                isAutoApproveCountdownLabel(conn.statusDate)
                                  ? "text-[8px] font-bold whitespace-nowrap leading-tight tracking-tight"
                                  : "text-[10px] max-w-[11rem] leading-snug"
                              )}
                            >
                              <AutoApproveStatusHint statusDate={conn.statusDate} />
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded border ${
                            conn.paid
                              ? "bg-emerald-50 text-emerald-600 border-emerald-200"
                              : "bg-amber-50 text-amber-600 border-amber-200"
                          }`}
                        >
                          {conn.paid ? "Paid" : "Unpaid"}
                        </span>
                        <div className="text-[10px] text-muted-foreground mt-0.5">{conn.method}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {conn.linkDisplayLabel === "Live" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded border bg-emerald-50 text-emerald-600 border-emerald-200">
                            Live
                          </span>
                        ) : conn.linkDisplayLabel === "Link Down" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded border bg-red-50 text-red-600 border-red-200">
                            Link Down
                          </span>
                        ) : conn.linkDisplayLabel === "Not Live" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded border bg-orange-50 text-orange-600 border-orange-200">
                            Not Live
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {filteredConnections.length > 0 && (
            <div className="shrink-0 border-t border-border px-6 py-4">
              <GlobalPagination
                total={total}
                pageSize={pageSize}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                onPageSizeChange={(size) => {
                  setPageSize(Number(size))
                  setCurrentPage(1)
                }}
                showPageSizeSelector={true}
                pageSizeOptions={[5, 10, 20, 50]}
              />
            </div>
          )}
        </div>

        {selectedConnection && (
          <ConnectionDetailsModal
            open={!!selectedConnection}
            onClose={() => setSelectedConnection(null)}
            connection={selectedConnection}
            orderId={orderId}
            zsOppFee={zsOppFee}
          />
        )}
      </DialogContent>
    </Dialog>
    </>
  )
}
